import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  ViewChild,
  ViewEncapsulation,
  inject,
} from "@angular/core";
import { MatTableDataSource, MatTableModule } from "@angular/material/table";
import { MatPaginator, MatPaginatorModule } from "@angular/material/paginator";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSort, MatSortModule, SortDirection } from "@angular/material/sort";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import {
  DEFAULT_SIGNER_ALLOWED_ACTIONS,
  getAllowedActionsDropdownOptionsForRole,
  ITableActionRow,
  REVIEWER_DEFAULT_ALLOWED_ACTIONS,
} from "@modals/app.modal";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { CommonModule } from "@angular/common";
import { TableDynamicInputComponent } from "../table-dynamic-input/table-dynamic-input.component";
import { TableService } from "@services/table.service";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { CdkDragDrop, DragDropModule, moveItemInArray } from "@angular/cdk/drag-drop";

interface IStoredTableSort {
  active: string;
  direction: SortDirection;
}

@Component({
  selector: "kzn-table",
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    CommonModule,
    TableDynamicInputComponent,
    TranslateModule,
    DragDropModule,
  ],
  templateUrl: "./table.component.html",
  styleUrl: "./table.component.scss",
  encapsulation: ViewEncapsulation.None,
})
export class TableComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input({ required: true }) data!: any[];
  @Input({ required: true }) columns!: any[];
  @Input() showRefresh: boolean = false;
  @Input() showSearch: boolean = true;
  @Input() showPagination: boolean = true;
  @Input() showExpandedRows: boolean = false;
  @Input() defaultSortColumnDef: string = "";
  @Input() defaultSortDirection: SortDirection = "";
  /** When set, table sort column/direction is persisted across refreshes. */
  @Input() sortStorageKey: string = "";
  /** Storage backend for sort persistence. Use `session` for tab-scoped sort state. */
  @Input() sortStorageScope: "local" | "session" = "local";
  @Input() rowActions: Array<ITableActionRow> = [];
  @Input() actionColumnHeader: string = "actions";
  /** When true, rows can be drag-reordered. Requires showOrderColumn for the order column. */
  @Input() enableRowReorder: boolean = false;
  /** When true, always show the order column; drag handle appears only if enableRowReorder is true. */
  @Input() showOrderColumn: boolean = false;
  @Output() refreshEventFired: EventEmitter<null> = new EventEmitter();
  public pageSizeOptions = [5, 10, 25, 50];
  public displayedColumns!: string[];
  public dataSource!: MatTableDataSource<any>;
  public expandedElement: any;
  public activeFilterText = "";
  public activeSortColumnDef = "";
  public activeSortDirection: SortDirection = "";

  private _cd: ChangeDetectorRef = inject(ChangeDetectorRef);
  private _translate = inject(TranslateService);
  private _langSub?: Subscription;
  private _sortSub?: Subscription;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild("input", { read: ElementRef }) tableInput!: ElementRef;
  scrollUp(): void {
    setTimeout(() =>
      this.tableInput.nativeElement.scrollIntoView({
        behavior: "smooth",
        block: "end",
      })
    );
  }

  constructor(public tableService: TableService) { }

  ngOnChanges(): void {
    if (this.data && this.columns) {
      this.displayedColumns = this.columns.map((c) => c.columnDef);
      if (this.showExpandedRows) {
        this.displayedColumns.unshift("expand");
      }
      if (this.showOrderColumn) {
        const insertAt = this.showExpandedRows ? 1 : 0;
        this.displayedColumns.splice(insertAt, 0, "reorder");
      }
      this.rowActions.length && this.displayedColumns.push("actions");
      this.dataSource = new MatTableDataSource<any>(this.data);
      this.dataSource.sortingDataAccessor = (data, columnDef) => {
        const isDate = this.columns.find(
          (column) => column.columnDef === columnDef
        )?.isDate;
        if (isDate) return new Date(data[columnDef]);
        else if (typeof data[columnDef] === 'string') return data[columnDef]?.toLowerCase();
        else return data[columnDef];
      };
      this.updatePaginatorAndSort();
      if (this.sort) {
        this.restoreSortState();
      }
    }
  }

  ngAfterViewInit() {
    this.updatePaginatorAndSort();
    this.restoreSortState();
    if (this.sortStorageKey && this.sort) {
      this._sortSub = this.sort.sortChange.subscribe(() => this.persistSortState());
    }
    this._langSub = this._translate.onLangChange.subscribe(() => this._cd.markForCheck());
    this._cd.detectChanges();
  }

  ngOnDestroy(): void {
    this._sortSub?.unsubscribe();
    this._langSub?.unsubscribe();
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.applyTextFilter(filterValue);
  }

  applyTextFilter(filterValue: string): void {
    this.activeFilterText = filterValue;
    if (!this.dataSource) return;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  private updatePaginatorAndSort() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  private restoreSortState(): void {
    if (!this.sort || !this.dataSource) {
      return;
    }

    let active = this.defaultSortColumnDef;
    let direction: SortDirection = this.defaultSortDirection || "";

    if (this.sortStorageKey) {
      const stored = this.readSortFromStorage();
      if (stored?.active) {
        active = stored.active;
        direction = stored.direction ?? direction;
      }
    }

    if (!active) {
      return;
    }

    this.sort.active = active;
    this.sort.direction = direction;
    this.activeSortColumnDef = active;
    this.activeSortDirection = direction;
    this.dataSource.sort = this.sort;
    this.dataSource.data = this.dataSource.sortData(this.dataSource.data, this.sort);
  }

  private readSortFromStorage(): IStoredTableSort | null {
    if (!this.sortStorageKey) {
      return null;
    }
    try {
      const raw = this.getSortStorage()?.getItem(this.sortStorageKey);
      if (!raw) {
        return null;
      }
      return JSON.parse(raw) as IStoredTableSort;
    } catch {
      return null;
    }
  }

  private persistSortState(): void {
    if (!this.sortStorageKey || !this.sort?.active) {
      return;
    }
    const payload: IStoredTableSort = {
      active: this.sort.active,
      direction: this.sort.direction,
    };
    this.activeSortColumnDef = this.sort.active;
    this.activeSortDirection = this.sort.direction;
    try {
      this.getSortStorage()?.setItem(this.sortStorageKey, JSON.stringify(payload));
    } catch {
      /* ignore quota / private mode */
    }
  }

  private getSortStorage(): Storage | null {
    if (typeof window === "undefined") {
      return null;
    }
    return this.sortStorageScope === "session" ? window.sessionStorage : window.localStorage;
  }

  onActionClick(action: ITableActionRow, row: unknown): void {
    if (action.isDisabled?.(row)) return;
    action.actionFn?.(row);
  }

  getActionTooltip(action: ITableActionRow, row: unknown): string {
    const key = action.getTooltip ? action.getTooltip(row) : action.title;
    const translated = this._translate.instant(key);
    if (typeof translated === "string" && translated !== key) {
      return translated;
    }
    const defaultLang = this._translate.getDefaultLang() || "en";
    const fallback = this._translate.translations[defaultLang]?.[key];
    if (typeof fallback === "string") {
      return fallback;
    }
    return key;
  }

  refreshTableData() {
    this.refreshEventFired.emit();
  }

  resolveColumnDropdownOptions(column: any, row: any): string[] {
    if (typeof column?.dropdownOptionsForRow === "function") {
      return column.dropdownOptionsForRow(row);
    }
    if (column?.columnDef === "allowedActions") {
      return [...getAllowedActionsDropdownOptionsForRole(row?.role)];
    }
    return column?.dropdownOptions ?? [];
  }

  onTableDynamicInputValueUpdate(value: string, key: string, row: any) {
    const index = this.tableService.tableInputData.findIndex(
      (item) => item === row || (row?.tempID && item?.tempID === row.tempID),
    );
    if (index < 0) return;

    const nextValue =
      key === "email" || key === "emailCc"
        ? value.trim().toLowerCase()
        : value;

    this.tableService.patchTableInputRow(index, key, nextValue);

    if (key === "role") {
      const role = nextValue.toString().trim().toLowerCase();
      const currentAllowedActions = this.tableService.tableInputData[index]?.allowedActions;
      if (role === "reviewer") {
        this.tableService.patchTableInputRow(
          index,
          "allowedActions",
          REVIEWER_DEFAULT_ALLOWED_ACTIONS
        );
      } else if (role === "signer") {
        const normalized = (currentAllowedActions ?? "").toString().trim();
        if (!normalized || normalized === REVIEWER_DEFAULT_ALLOWED_ACTIONS) {
          this.tableService.patchTableInputRow(
            index,
            "allowedActions",
            DEFAULT_SIGNER_ALLOWED_ACTIONS
          );
        }
      }
      this._cd.detectChanges();
    }
  }

  onRowReorderDrop(event: CdkDragDrop<any[]>): void {
    if (!this.enableRowReorder || event.previousIndex === event.currentIndex) {
      return;
    }

    const rows = [...(event.container.data as any[])];
    moveItemInArray(rows, event.previousIndex, event.currentIndex);
    this.tableService.syncSignerOrders(rows);
    this.dataSource.data = rows;
    this.tableService.tableInputData = rows;
    this._cd.detectChanges();
  }
}
